import React from 'react';

interface CategoriesMissionTabProps {
  t: (key: string) => string;
  loading: boolean;
  categoriesMission: { id: number; nom: string; created_at?: string }[];
  newCategoryName: string;
  setNewCategoryName: React.Dispatch<React.SetStateAction<string>>;
  editingMissionCategoryId: number | null;
  creatingCategory: boolean;
  handleSaveMissionCategory: () => void;
  handleCancelEditMissionCategory: () => void;
  handleEditMissionCategory: (c: { id: number; nom: string }) => void;
  handleDeleteMissionCategory: (id: number) => void;
}

const CategoriesMissionTab: React.FC<CategoriesMissionTabProps> = ({
  t,
  loading,
  categoriesMission,
  newCategoryName,
  setNewCategoryName,
  editingMissionCategoryId,
  creatingCategory,
  handleSaveMissionCategory,
  handleCancelEditMissionCategory,
  handleEditMissionCategory,
  handleDeleteMissionCategory,
}) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-sm font-semibold text-slate-200 mb-3">
        {editingMissionCategoryId ? t('categoriesMission.editTitle') : t('categoriesMission.addTitle')}
      </h2>
      <p className="text-xs text-slate-400 mb-3">{t('categoriesMission.addDescription')}</p>
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <input
          type="text"
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          placeholder={t('categoriesMission.placeholder')}
          className="flex-1 min-w-[200px] rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSaveMissionCategory}
            disabled={creatingCategory}
            className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-5 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors disabled:opacity-50"
          >
            {creatingCategory
              ? t('categoriesMission.saving')
              : editingMissionCategoryId
                ? t('categoriesMission.update')
                : t('categoriesMission.add')}
          </button>
          {editingMissionCategoryId && (
            <button
              type="button"
              onClick={handleCancelEditMissionCategory}
              disabled={creatingCategory}
              className="rounded-lg border border-white/15 bg-black/30 px-5 py-2 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors disabled:opacity-50"
            >
              {t('categoriesMission.cancel')}
            </button>
          )}
        </div>
      </div>
    </div>
    <div className="border-t border-[#1e2d4d] pt-4">
      <h2 className="text-sm font-semibold text-slate-200 mb-3">{t('categoriesMission.listTitle')}</h2>
      {loading ? (
        <p className="text-sm text-slate-500">{t('categoriesMission.loading')}</p>
      ) : categoriesMission.length === 0 ? (
        <p className="text-sm text-slate-500">{t('categoriesMission.empty')}</p>
      ) : (
        <ul className="divide-y divide-[#1e2d4d]">
          {[...categoriesMission].sort((a, b) => a.nom.localeCompare(b.nom, 'fr')).map((c) => (
            <li
              key={c.id}
              className={`py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${editingMissionCategoryId === c.id ? 'rounded-lg bg-[#3d4fdb]/10 px-3 -mx-1' : ''}`}
            >
              <div className="flex flex-wrap justify-between items-center gap-2 text-slate-200 flex-1 min-w-0">
                <span className="font-medium">{c.nom}</span>
                <span className="text-xs text-slate-500 shrink-0">ID: {c.id}</span>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => handleEditMissionCategory(c)}
                  disabled={!!creatingCategory}
                  className="rounded border border-white/15 bg-black/30 px-2 py-1 text-xs font-medium text-slate-200 hover:bg-black/45 transition-colors disabled:opacity-50"
                >
                  {t('categoriesMission.modify')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteMissionCategory(c.id)}
                  disabled={!!creatingCategory}
                  className="rounded border border-red-500/35 bg-red-500/10 px-2 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                >
                  {t('categoriesMission.delete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

export default CategoriesMissionTab;
